import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { AppErrorBoundary } from './AppErrorBoundary'
import { BrowserRouter, useLocation } from 'react-router-dom'
import reportWebVitals from './reportWebVitals'
import posthog, { CaptureResult } from 'posthog-js'
import { maskSensitiveReplayInput, scrubReplayNetworkRequest } from 'ui'
import { PostHogProvider } from 'posthog-js/react'
import { getFrameOrigin } from './lib/urls'
import Sentry from './lib/sentry'
import { reloadOnStaleChunk } from './lib/staleChunkReload'
import { tolerateForeignDomEdits } from './lib/tolerateForeignDomEdits'
import {
  scrubReplayUrl,
  scrubSensitiveEvent,
  scrubStoredInitialPersonInfo,
} from './lib/posthogUrlScrub'
import {
  initialUtmPersonProperties,
  rememberVisitUtmParams,
  utmParamsFromSearch,
} from './lib/utmParams'

// $exception messages that are environment noise, dropped before ingestion:
// - "ResizeObserver loop…": a harmless relayout warning; a wedged tab can emit
//   it at ~1/s for hours.
// - "Object Not Found Matching Id…": Outlook's link scanner opening emailed
//   links, never a real user.
const NOISE_EXCEPTION_MESSAGES = [
  'ResizeObserver loop',
  'Object Not Found Matching Id',
]

const dropNoiseExceptions = (
  event: CaptureResult | null,
): CaptureResult | null => {
  if (event?.event !== '$exception') return event
  const exceptions: { value?: string }[] =
    event.properties?.$exception_list ?? []
  return exceptions.some((e) =>
    NOISE_EXCEPTION_MESSAGES.some((noise) => e.value?.includes(noise)),
  )
    ? null
    : event
}

// The sdk-frame proxy iframe is on the critical path for AP / payment-link
// flows, so warm its connection up front. Vite's index.html is static, so
// this stands in for the portal's preconnect in `_document.tsx`.
if (typeof document !== 'undefined') {
  const link = document.createElement('link')
  link.rel = 'preconnect'
  link.href = getFrameOrigin()
  document.head.appendChild(link)
}

if (typeof window !== 'undefined' && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
    ui_host:
      import.meta.env.NODE === 'development'
        ? undefined
        : 'https://us.posthog.com',
    loaded: () => {
      // Before the first /flags request, which sends a first URL stored on
      // an earlier visit as an $initial_* person property.
      scrubStoredInitialPersonInfo(posthog.persistence)
      if (import.meta.env.MODE === 'development') {
        console.log('Posthog loaded')
      }
    },
    session_recording: {
      maskAllInputs: true,
      maskInputFn: maskSensitiveReplayInput,
      maskCapturedNetworkRequestFn: scrubReplayUrl(scrubReplayNetworkRequest),
    },
    capture_pageview: false,
    capture_exceptions: true,
    before_send: [
      dropNoiseExceptions,
      (event) => {
        scrubStoredInitialPersonInfo(posthog.persistence)
        return scrubSensitiveEvent(event)
      },
    ],
  })
}

export default function PostHogPageView() {
  const location = useLocation()

  // utm_* are read off each pageview's own URL because posthog-js reads them
  // only on the first page load, which for a logged-out email click is
  // /login (lib/utmParams.ts).
  useEffect(() => {
    rememberVisitUtmParams(window.location.search)
    if (posthog) {
      const utm = utmParamsFromSearch(window.location.search)
      const hasUtm = Object.keys(utm).length > 0
      posthog.capture(
        '$pageview',
        { $current_url: window.location.href, ...utm },
        hasUtm ? { $set_once: initialUtmPersonProperties(utm) } : undefined,
      )
    }
  }, [location])

  return null
}

reloadOnStaleChunk()

tolerateForeignDomEdits((error, context) => {
  posthog.captureException(error, context)
  Sentry.captureException(error, { extra: context })
})

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

const AppComponent = () => (
  <React.StrictMode>
    <BrowserRouter>
      <PostHogProvider client={posthog}>
        <AppErrorBoundary>
          <App />
          <PostHogPageView />
        </AppErrorBoundary>
      </PostHogProvider>
    </BrowserRouter>
  </React.StrictMode>
)

root.render(<AppComponent />)

reportWebVitals()
